import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PublicUser, SessionCompany, UserRole } from '@fsm/shared';
import { api, setUnauthenticatedHandler } from './api';

// Derived, not re-declared: this used to be a hand-written copy of the same
// three fields, and it broke the build the day a fourth was added.
type Company = SessionCompany;

interface AuthState {
  user: PublicUser | null;
  company: Company | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (email: string, password: string) => Promise<void>;
  /** Create a company and its first admin, then sign in as them. */
  signUp: (input: {
    companyName: string;
    name: string;
    email: string;
    password: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  can: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const signOut = useCallback(async () => {
    await api.auth.logout().catch(() => {});
    setUser(null);
    setCompany(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    // A refresh that fails for real (not a network blip) ends the session.
    setUnauthenticatedHandler(() => {
      setUser(null);
      setCompany(null);
      setStatus('anonymous');
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const tokens = await api.tokens.get();
      if (!tokens) {
        if (!cancelled) setStatus('anonymous');
        return;
      }
      try {
        const me = await api.auth.me();
        if (cancelled) return;
        setUser(me.user);
        setCompany(me.company);
        setStatus('authenticated');
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.auth.login(email, password, { platform: 'web' });
    setUser(result.user);
    setCompany(result.company);
    setStatus('authenticated');
  }, []);

  const signUp = useCallback(async (input: {
    companyName: string;
    name: string;
    email: string;
    password: string;
  }) => {
    const result = await api.auth.signup(input);
    setUser(result.user);
    setCompany(result.company);
    setStatus('authenticated');
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      company,
      status,
      signIn,
      signUp,
      signOut,
      can: (...roles: UserRole[]) => Boolean(user && roles.includes(user.role)),
    }),
    [user, company, status, signIn, signUp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
